/* globals svgEditor */
import insertImageDialogHTML from './insertImageDialog.html'
import { allCapsShadowCss } from '../components/allCapsShadowCss.js'

const template = document.createElement('template')
template.innerHTML = allCapsShadowCss + insertImageDialogHTML

/**
 * Insert-image dialog: three source modes (local file, clipboard paste, URL).
 * Emits a `change` CustomEvent with detail { trigger, mode, files, dataUrl, url, dialog }.
 * @class SeInsertImageDialog
 */
export class SeInsertImageDialog extends HTMLElement {
  /**
    * @function constructor
    */
  constructor () {
    super()
    this._shadowRoot = this.attachShadow({ mode: 'open' })
    this._shadowRoot.append(template.content.cloneNode(true))
    this.$dialog = this._shadowRoot.querySelector('#insert_image_box')
    this.$okBtn = this._shadowRoot.querySelector('#ii_ok')
    this.$cancelBtn = this._shadowRoot.querySelector('#ii_cancel')
    this.$tabs = [...this._shadowRoot.querySelectorAll('.ii-tab')]
    this.$pages = [...this._shadowRoot.querySelectorAll('.ii-page')]
    this.$fileInput = this._shadowRoot.querySelector('#ii_file_input')
    this.$fileHint = this._shadowRoot.querySelector('#ii_file_hint')
    this.$pasteHint = this._shadowRoot.querySelector('#ii_paste_hint')
    this.$pastePreview = this._shadowRoot.querySelector('#ii_paste_preview')
    this.$urlInput = this._shadowRoot.querySelector('#ii_url_input')
    this.$urlNote = this._shadowRoot.querySelector('#ii_url_note')
    this.$urlHint = this._shadowRoot.querySelector('#ii_url_hint')
    this._mode = 'file'
    this._files = null
    this._dataUrl = null
    // Document-level paste listener lives while the dialog is open
    this._onDocPaste = (e) => this._handlePaste(e)
  }

  /**
   * @function init
   * @param {any} i18next
   * @returns {void}
   */
  init (i18next) {
    this.setAttribute('common-ok', i18next.t('common.ok'))
    this.setAttribute('common-cancel', i18next.t('common.cancel'))
    this.setAttribute('tools-insert_image', i18next.t('tools.insert_image'))
    this.setAttribute('ii-tab_file', i18next.t('ii.tab_file'))
    this.setAttribute('ii-tab_paste', i18next.t('ii.tab_paste'))
    this.setAttribute('ii-tab_url', i18next.t('ii.tab_url'))
    this.setAttribute('ii-file_hint', i18next.t('ii.file_hint'))
    this.setAttribute('ii-paste_hint', i18next.t('ii.paste_hint'))
    this.setAttribute('ii-url_hint', i18next.t('ii.url_hint'))
    this.setAttribute('ii-url_note', i18next.t('ii.url_note'))
    this.setAttribute('ii-file_selected', i18next.t('ii.file_selected'))
    this.setAttribute('ii-pasted_ok', i18next.t('ii.pasted_ok'))
    this.setAttribute('ii-paste_empty', i18next.t('ii.paste_empty'))
    this.setAttribute('ii-url_empty', i18next.t('ii.url_empty'))
    this.setAttribute('aria-label', i18next.t('tools.insert_image'))
  }

  /**
   * @function observedAttributes
   * @returns {string[]}
   */
  static get observedAttributes () {
    return ['dialog', 'common-ok', 'common-cancel', 'aria-label',
      'ii-tab_file', 'ii-tab_paste', 'ii-tab_url', 'ii-file_hint',
      'ii-paste_hint', 'ii-url_hint', 'ii-url_note', 'ii-file_selected',
      'ii-pasted_ok', 'ii-paste_empty', 'ii-url_empty']
  }

  /**
   * @function attributeChangedCallback
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   * @returns {void}
   */
  attributeChangedCallback (name, oldValue, newValue) {
    if (oldValue === newValue) return
    let node
    switch (name) {
      case 'dialog':
        if (newValue === 'open') {
          this._reset()
          this.$dialog.open()
          document.addEventListener('paste', this._onDocPaste)
        } else {
          this.$dialog.close()
          document.removeEventListener('paste', this._onDocPaste)
        }
        break
      case 'common-ok':
        this.$okBtn.textContent = newValue
        break
      case 'common-cancel':
        this.$cancelBtn.textContent = newValue
        break
      case 'ii-tab_file':
      case 'ii-tab_paste':
      case 'ii-tab_url':
        node = this._shadowRoot.querySelector(`#ii_tab_${name.split('ii-tab_')[1]}`)
        if (node) node.textContent = newValue
        break
      case 'ii-file_hint':
        this.$fileHint.textContent = newValue
        break
      case 'ii-paste_hint':
        this.$pasteHint.textContent = newValue
        break
      case 'ii-url_hint':
        this.$urlHint.textContent = newValue
        break
      case 'ii-url_note':
        this.$urlNote.textContent = newValue
        break
      case 'ii-file_selected':
        this._msgFileSelected = newValue
        break
      case 'ii-pasted_ok':
        this._msgPastedOk = newValue
        break
      case 'ii-paste_empty':
        this._msgPasteEmpty = newValue
        break
      case 'ii-url_empty':
        this._msgUrlEmpty = newValue
        break
      case 'aria-label':
        this.$dialog.setAttribute('aria-label', newValue)
        break
      default:
        break
    }
  }

  /**
   * Clears pending selection state.
   * @returns {void}
   */
  _reset () {
    this._files = null
    this._dataUrl = null
    this.$fileInput.value = ''
    this.$urlInput.value = ''
    this.$pastePreview.innerHTML = ''
    this.$fileHint.textContent = this.getAttribute('ii-file_hint') ?? ''
    this.$pasteHint.textContent = this.getAttribute('ii-paste_hint') ?? ''
    this._switchTab('file')
  }

  /**
   * @param {string} mode 'file' | 'paste' | 'url'
   * @returns {void}
   */
  _switchTab (mode) {
    this._mode = mode
    this.$tabs.forEach((t) => {
      const active = t.dataset.tab === mode
      t.classList.toggle('ii-active', active)
      t.setAttribute('aria-selected', active ? 'true' : 'false')
    })
    this.$pages.forEach((p) => p.classList.toggle('ii-page-active', p.id === `ii_page_${mode}`))
  }

  /**
   * @param {string} text feedback message
   * @returns {void}
   */
  _showPasteInfo (text) {
    this.$pasteHint.textContent = text
  }

  /**
   * Accepts an image extracted from the clipboard.
   * @param {Blob|File} blob image blob
   * @returns {Promise<void>}
   */
  async _acceptImageBlob (blob) {
    if (!blob || !blob.type.startsWith('image/')) {
      this._showPasteInfo(this._msgPasteEmpty)
      return
    }
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = () => reject(reader.error ?? new Error('read error'))
        reader.readAsDataURL(blob)
      })
      this._dataUrl = dataUrl
      const img = document.createElement('img')
      img.src = dataUrl
      img.alt = ''
      this.$pastePreview.innerHTML = ''
      this.$pastePreview.append(img)
      this._showPasteInfo(this._msgPastedOk)
    } catch (err) {
      console.error('insertImageDialog: cannot read clipboard image', err)
      this._showPasteInfo(this._msgPasteEmpty)
    }
  }

  /**
   * @param {ClipboardEvent} e
   * @returns {void}
   */
  _handlePaste (e) {
    if (this._mode !== 'paste') return
    e.stopPropagation()
    e.preventDefault()
    const items = e.clipboardData?.items ?? []
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        this._acceptImageBlob(item.getAsFile())
        return
      }
    }
    this._showPasteInfo(this._msgPasteEmpty)
  }

  /**
   * @function get
   * @returns {any}
   */
  get dialog () {
    return this.getAttribute('dialog')
  }

  /**
   * @function set
   * @returns {void}
   */
  set dialog (value) {
    this.setAttribute('dialog', value)
  }

  /**
   * @function connectedCallback
   * @returns {void}
   */
  connectedCallback () {
    this.$tabs.forEach((tab) => {
      svgEditor.$click(tab, () => this._switchTab(tab.dataset.tab))
    })
    this.$fileInput.addEventListener('change', () => {
      const files = Array.from(this.$fileInput.files ?? []).filter((f) => f.type.startsWith('image/'))
      if (files.length === 0) return
      this._files = files
      this.$fileHint.textContent = `${this._msgFileSelected}: ${files.map((f) => f.name).join(', ')}`
    })
    this._onOk = () => {
      const detail = { trigger: 'ok', mode: this._mode, dialog: 'closed' }
      if (this._mode === 'file') {
        if (!this._files?.length) return
        detail.files = this._files
      } else if (this._mode === 'paste') {
        if (!this._dataUrl) return
        detail.dataUrl = this._dataUrl
      } else {
        const url = this.$urlInput.value.trim()
        if (!url) return
        detail.url = url
      }
      this.dispatchEvent(new CustomEvent('change', { detail }))
      document.getElementById('se-insert-image-dialog').setAttribute('dialog', 'close')
    }
    this._onCancel = () => {
      this.dispatchEvent(new CustomEvent('change', { detail: { trigger: 'cancel', dialog: 'closed' } }))
      document.getElementById('se-insert-image-dialog').setAttribute('dialog', 'close')
    }
    svgEditor.$click(this.$okBtn, this._onOk)
    svgEditor.$click(this.$cancelBtn, this._onCancel)
    this.$dialog.addEventListener('close', this._onCancel)
  }
}

// Register
customElements.define('se-insert-image-dialog', SeInsertImageDialog)
